// ---------------------------------------------------------------------------
// โหมด mock — handler ต่อ action (ชื่อเดียวกับ ACTIONS ใน api.js) เก็บใน localStorage ชุด `aitalk:mock:*`
// ใช้เมื่อ IS_MOCK (ไม่ตั้ง REACT_APP_GS_URL) — ไม่ยิงเน็ตเลย
// ---------------------------------------------------------------------------
import { MOCK_LATENCY, MOCK_PREFIX } from "./config";
import { getStoredProfile } from "./apiClient";
import {
  SAMPLE_CHARACTERS, SAMPLE_UNIVERSES, SAMPLE_CREATORS, SAMPLE_PACKAGES, AI_MODELS, SAMPLE_GIFTS,
  SAMPLE_POSTS, AI_REPLIES, MOCK_TIERS, ROMANTIC_LEVELS, INTIMACY_LEVELS, SAMPLE_PERSONAS, SAMPLE_SCENARIOS,
} from "./mockData";
import { parseTiers, coinCalc, creditCalc, creditPresets } from "./fb/pricing";
import { giftTier, giftIntent, nextCombo, giftRelDelta } from "./gift";
import { answerIntent } from "./ai/retrieval";
import { resolvePersona } from "./ai/persona";

/** ค่าตั้งราคาเริ่มต้น = DEFAULT_SETTINGS ของโหมด Firebase */
const MOCK_PRICING = {
  coin: { perBaht: 10, min: 300, max: 50000, bonusTiers: "500:10,1000:20,2000:30,5000:50" },
  credit: { price: 300, max: 100, tiers: "1:0,5:5,10:10,30:12,50:15,100:20", required: true },
};

const k = (name) => `${MOCK_PREFIX}:mock:${name}`;
export const readStore = (name, fallback) => {
  try {
    const raw = localStorage.getItem(k(name));
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
};
export const writeStore = (name, value) => localStorage.setItem(k(name), JSON.stringify(value));

const uid = (p) => `${p}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
// "วัน" ของเช็คอิน/สตรีคคิดตามเวลาไทย (UTC+7) เหมือน GS
const thaiDay = (offset = 0) => new Date(Date.now() + 7 * 3600e3 + offset * 864e5).toISOString().slice(0, 10);
const today = () => thaiDay(0);
/** error พร้อม code เหมือน gsClient (insufficient_coins, ai_busy, payment_pending, already_done ...) */
const fail = (msg, code) => {
  const e = new Error(msg);
  if (code) e.code = code;
  throw e;
};

// ---- ผู้ใช้ (ใช้ร่วมกับ auth.js โหมด mock) --------------------------------
export const mockUsers = {
  all: () => readStore("users", {}),
  get: (email) => mockUsers.all()[String(email || "").toLowerCase()] || null,
  save: (user) => {
    const all = mockUsers.all();
    all[user.email.toLowerCase()] = user;
    writeStore("users", all);
    return user;
  },
  create: ({ email, displayName, password, phone }) => {
    const lower = email.toLowerCase();
    return mockUsers.save({
      id: uid("u_"),
      email: lower,
      password: btoa(password || ""),
      phone: phone || "",
      displayName: displayName || lower.split("@")[0],
      username: lower.split("@")[0].replace(/[^a-z0-9_]/g, "").slice(0, 20) || uid("user"),
      user_type: lower.startsWith("admin") ? "super_user" : "customer",
      tokensLeft: 50,
      tier: "Rookie",
      clubPoints: 0,
      termsAcceptedAt: null,
      invitationCode: Math.random().toString(36).slice(2, 10).toUpperCase(),
      dailyCheckIn: { streak: 0, longestStreak: 0, lastCheckInDate: "", checkInDates: [] },
      createdAt: new Date().toISOString(),
    });
  },
};

const me = () => {
  const email = getStoredProfile()?.email;
  const user = mockUsers.get(email);
  if (!user) fail("กรุณาเข้าสู่ระบบ");
  return user;
};
const patchMe = (patch) => mockUsers.save({ ...me(), ...patch });

const addCoinTx = (user, tx) => {
  const list = readStore("coinTx", []);
  list.unshift({ id: uid("tx_"), userId: user.id, createdAt: new Date().toISOString(), ...tx });
  writeStore("coinTx", list.slice(0, 300));
};
const spend = (amount, type, extra = {}) => {
  const user = me();
  if (user.tokensLeft < amount) fail(`คอยน์ไม่พอ ต้องการ ${amount} คอยน์ แต่มี ${user.tokensLeft} คอยน์`, "insufficient_coins");
  const next = patchMe({ tokensLeft: user.tokensLeft - amount });
  addCoinTx(user, { usageType: type, amount: -amount, coinUsed: -amount, balanceBefore: user.tokensLeft, balanceAfter: next.tokensLeft, ...extra });
  return next;
};
const earn = (amount, type, extra = {}) => {
  const user = me();
  const next = patchMe({ tokensLeft: user.tokensLeft + amount });
  addCoinTx(user, { usageType: type, amount, coinUsed: amount, balanceBefore: user.tokensLeft, balanceAfter: next.tokensLeft, ...extra });
  return next;
};

const creatorOf = (id) => SAMPLE_CREATORS.find((c) => c.id === id);
const withCreator = (c) => ({ ...c, creator: creatorOf(c.creatorId) });
const myCharacters = () => readStore("myCharacters", []);
const allCharacters = () => [...myCharacters(), ...SAMPLE_CHARACTERS].map(withCreator);

const paginate = (rows, { page = 1, limit = 24 } = {}) => {
  const p = Number(page) || 1;
  const l = Number(limit) || 24;
  const start = (p - 1) * l;
  return { items: rows.slice(start, start + l), page: p, limit: l, count: rows.length, hasMore: start + l < rows.length };
};

const sorters = {
  trending: (a, b) => b.likeCount - a.likeCount,
  new: (a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")),
  popular: (a, b) => b.messageCount - a.messageCount,
  following: () => 0,
};

// ---- helper แชท/คอยน์/VIP (ใช้ใน handlers ด้านล่าง) -------------------------
const levelLabel = (levels, v) => {
  let out = levels[0][1];
  levels.forEach(([min, name]) => { if (v >= min) out = name; });
  return out;
};
const clampRel = (v) => Math.max(-1000, Math.min(1000, v));
const allChats = () => readStore("chats", []);
const myChat = (chatId) => {
  const c = allChats().find((x) => x.id === chatId && !x.isDeleted);
  if (!c || c.userId !== me().id) fail("ไม่พบแชท", "not_found");
  return c;
};
const saveChat = (chatId, patch) => {
  const list = allChats().map((c) => (c.id === chatId ? { ...c, ...patch } : c));
  writeStore("chats", list);
  return list.find((c) => c.id === chatId);
};
const charOf = (id) => allCharacters().find((x) => x.id === id);
const charCard = (id) => {
  const ch = charOf(id);
  return ch ? { id: ch.id, name: ch.name, tagline: ch.tagline, image: ch.image || null, emoji: ch.emoji, colors: ch.colors, creatorName: ch.creator?.displayName || null } : null;
};
const modelOf = (id) => AI_MODELS.find((m) => m.id === id) || AI_MODELS.find((m) => m.isDefault) || AI_MODELS[0];
const myPersonas = () => readStore("personas", []);
const myScenarios = () => readStore("scenarios", []);
const personaOf = (id) => (id ? [...myPersonas(), ...SAMPLE_PERSONAS].find((x) => x.id === id) || null : null);
const scenarioOf = (id) => (id ? [...myScenarios(), ...SAMPLE_SCENARIOS].find((x) => x.id === id) || null : null);
const DEFAULT_CHAT_SETTINGS = { bannedWords: [], langGuard: true, shortcuts: [] };
const msgsOf = (chatId) => readStore(`msgs:${chatId}`, []);
const streakUpdate = (c) => {
  const t = today();
  const count = Number(c.streakCount) || 0;
  if (c.streakLastDate === t) return { streakCount: count || 1, streakBest: Math.max(c.streakBest || 0, count || 1), streakLastDate: t };
  const next = c.streakLastDate === thaiDay(-1) ? count + 1 : 1;
  return { streakCount: next, streakBest: Math.max(c.streakBest || 0, next), streakLastDate: t };
};

/** สถานะ VIP รูปเดียวกับ vip.status ของ GS (ระดับ = ค่าสูงสุดของพ้อยท์เดือนนี้/เดือนก่อน) */
const vipOf = (u) => {
  const mk = today().slice(0, 7);
  const cur = u.clubPoints || 0;
  const prev = u.clubPrevPoints || 0;
  const eff = Math.max(cur, prev);
  let idx = 0;
  MOCK_TIERS.forEach((t, i) => { if (eff >= t.minPoints) idx = i; });
  const claimed = (u.monthlyClaims && u.monthlyClaims[mk]) || [];
  const claimable = MOCK_TIERS.filter((t, i) => i <= idx && t.monthlyCoins > 0 && !claimed.includes(t.id));
  return {
    tier: MOCK_TIERS[idx], tierIndex: idx, nextTier: MOCK_TIERS[idx + 1] || null, tiers: MOCK_TIERS,
    currentMonthPoints: cur, previousMonthPoints: prev, effectivePoints: eff,
    canClaimMonthly: claimable.length > 0, monthlyCoins: claimable.reduce((s, t) => s + t.monthlyCoins, 0), claimable, month: mk,
  };
};
const streakNow = (c) => (c.lastCheckInDate === today() || c.lastCheckInDate === thaiDay(-1) ? c.streak || 0 : 0);
const checkinReward = (u, streak) => {
  const v = vipOf(u);
  return { base: Math.round(30 * v.tier.checkinMultiplier), bonus: streak % 10 === 0 ? (v.tierIndex >= 1 ? 60 : 30) : 0 };
};
const creditPackage = (pkg, txId) => {
  const user = earn(pkg.coins, "purchase", { packageId: pkg.id, refId: txId, note: `เติมเงิน ${pkg.name}` });
  const pts = (user.clubPoints || 0) + pkg.vipPoints;
  patchMe({ clubPoints: pts });
  patchMe({ tier: vipOf({ ...me(), clubPoints: pts }).tier.name });
  return me();
};

export const handlers = {
  // ---- user -----------------------------------------------------------
  "user.me": () => {
    const { password, ...rest } = me(); // eslint-disable-line no-unused-vars
    return rest;
  },
  "user.update": ({ data }) => {
    const { displayName, username, description } = data || {};
    return patchMe({
      ...(displayName ? { displayName: String(displayName).slice(0, 20) } : {}),
      ...(username ? { username: String(username).slice(0, 20) } : {}),
      ...(description !== undefined ? { description: String(description).slice(0, 500) } : {}),
    });
  },
  "user.acceptTerms": ({ version }) => patchMe({ termsAcceptedAt: new Date().toISOString(), termsVersion: version }),
  "user.preferences": ({ data }) => patchMe({ preferences: { ...(me().preferences || {}), ...data } }),

  // ---- characters -----------------------------------------------------
  "chars.list": ({ sort = "trending", tags, q, page, limit }) => {
    let rows = allCharacters();
    const tagList = String(tags || "").split(",").filter(Boolean);
    if (tagList.length) rows = rows.filter((c) => tagList.every((t) => c.tags.includes(t)));
    if (q) rows = rows.filter((c) => (c.name + c.tagline + c.tags.join(" ")).toLowerCase().includes(String(q).toLowerCase()));
    rows = [...rows].sort(sorters[sort] || sorters.trending);
    return paginate(rows, { page, limit });
  },
  "chars.get": ({ id }) => {
    const c = allCharacters().find((x) => x.id === id) || fail("ไม่พบตัวละครนี้");
    return { ...c, isLiked: readStore("likes", []).includes(id) };
  },
  "chars.like": ({ id }) => {
    const likes = readStore("likes", []);
    const next = likes.includes(id) ? likes.filter((x) => x !== id) : [...likes, id];
    writeStore("likes", next);
    return { liked: next.includes(id) };
  },
  noop: () => ({ ok: true }),
  "follow.toggle": ({ userId }) => {
    const f = readStore("follows", []);
    const next = f.includes(userId) ? f.filter((x) => x !== userId) : [...f, userId];
    writeStore("follows", next);
    return { following: next.includes(userId) };
  },
  "chars.create": ({ data }) => {
    if (!data?.name || !data?.tagline) fail("กรุณากรอกชื่อและคำโปรย");
    const user = me();
    const row = {
      id: uid("c_"), emoji: "✨", colors: ["#8b5cf6", "#ec4899"], tags: ["PG"], messageCount: 0, likeCount: 0, chatCount: 0,
      features: [], createdAt: new Date().toISOString(), ...data, creatorId: user.id,
    };
    writeStore("myCharacters", [row, ...myCharacters()]);
    return row;
  },
  "chars.myList": () => myCharacters(),
  "chars.drafts": () => readStore("drafts", []),

  // ---- universes / creators / ranking ----------------------------------
  "univ.list": ({ page, limit }) =>
    paginate(SAMPLE_UNIVERSES.map((u) => ({ ...u, creator: creatorOf(u.creatorId), characterCount: u.characterIds.length })), { page, limit }),
  "univ.get": ({ id }) => {
    const u = SAMPLE_UNIVERSES.find((x) => x.id === id) || fail("ไม่พบจักรวาลนี้");
    return { ...u, creator: creatorOf(u.creatorId), characters: u.characterIds.map((cid) => SAMPLE_CHARACTERS.find((c) => c.id === cid)).filter(Boolean) };
  },
  "creators.list": ({ q }) => ({
    items: SAMPLE_CREATORS.filter((c) => !q || (c.displayName + c.username).toLowerCase().includes(String(q).toLowerCase())),
  }),
  "creators.get": ({ id }) => {
    const c = SAMPLE_CREATORS.find((x) => x.id === id || x.username === id) || fail("ไม่พบครีเอเตอร์นี้");
    return { ...c, isFollowing: readStore("follows", []).includes(c.id), characters: SAMPLE_CHARACTERS.filter((x) => x.creatorId === c.id) };
  },
  "ranking.overview": () => ({
    period: "week",
    characters: [...SAMPLE_CHARACTERS].sort(sorters.popular).slice(0, 10).map((c, i) => ({ rank: i + 1, id: c.id, name: c.name, emoji: c.emoji, colors: c.colors, value: c.messageCount })),
    universes: SAMPLE_UNIVERSES.map((u, i) => ({ rank: i + 1, id: u.id, name: u.name, emoji: u.emoji, colors: u.colors, value: u.messageCount })),
    creators: SAMPLE_CREATORS.map((c, i) => ({ rank: i + 1, id: c.id, name: c.displayName, colors: c.colors, value: c.followerCount })),
  }),

  // ---- chats (รูปแบบเดียวกับ GS) -----------------------------------------
  bootstrap: () => ({ packages: SAMPLE_PACKAGES, models: AI_MODELS, gifts: SAMPLE_GIFTS, vipTiers: MOCK_TIERS }),
  "chats.list": ({ archived, q, cursor, limit = 30 }) => {
    const me0 = me();
    const query = String(q || "").toLowerCase();
    const rows = allChats()
      .filter((c) => c.userId === me0.id && !c.isDeleted && Boolean(c.isArchived) === Boolean(archived))
      .filter((c) => !query || (String(c.name) + (charOf(c.characterId)?.name || "")).toLowerCase().includes(query))
      .sort((a, b) => Number(b.isPinned) - Number(a.isPinned) || String(b.latestMessageTime).localeCompare(String(a.latestMessageTime)));
    const off = Number(cursor) || 0;
    const page = rows.slice(off, off + limit);
    return { items: page.map((c) => ({ ...c, character: charCard(c.characterId) })), nextCursor: off + limit < rows.length ? String(off + limit) : null };
  },
  "chat.create": ({ characterId, personaId, scenarioId, modelId, name }) => {
    const ch = charOf(characterId) || fail("ไม่พบตัวละครนี้", "not_found");
    const user = me();
    const now = new Date().toISOString();
    const persona = personaOf(personaId);
    const greeting = String(ch.greeting || "สวัสดี").replaceAll("{{user}}", persona?.name || user.displayName).replaceAll("{{char}}", ch.name);
    const chat = {
      id: uid("ch_"), userId: user.id, characterId, name: String(name || ch.name).slice(0, 40), personaId: personaId || null, scenarioId: scenarioId || null,
      modelId: modelOf(modelId).id, relationshipMode: false, romanticValue: 0, intimacyValue: 0, isPinned: false, isArchived: false, isRead: true, isDeleted: false,
      latestMessageTime: now, lastPreview: greeting.slice(0, 60), messageCount: 1, streakCount: 0, streakBest: 0, streakLastDate: null,
      settings: { ...DEFAULT_CHAT_SETTINGS }, createdAt: now,
    };
    writeStore("chats", [chat, ...allChats()]);
    const first = { id: uid("m_"), chatId: chat.id, sender: "character", type: "text", content: greeting, createdAt: now };
    writeStore(`msgs:${chat.id}`, [first]);
    return { chat: { ...chat, character: charCard(characterId) }, messages: [first] };
  },
  "chat.get": ({ id }) => {
    const chat = myChat(id);
    const days = Math.max(1, Math.floor((Date.now() - new Date(chat.createdAt).getTime()) / 864e5) + 1);
    const mem = readStore(`memory:${id}`, null);
    return {
      chat, character: charCard(chat.characterId), persona: personaOf(chat.personaId), scenario: scenarioOf(chat.scenarioId), model: modelOf(chat.modelId),
      memory: mem, memoryLimit: vipOf(me()).tier.noteLimit,
      stats: { days, streakCount: chat.streakCount || 0, streakBest: chat.streakBest || 0, messageCount: chat.messageCount || 0, since: chat.createdAt },
      relationship: {
        romanticValue: chat.romanticValue || 0, romanticLabel: levelLabel(ROMANTIC_LEVELS, chat.romanticValue || 0),
        intimacyValue: chat.intimacyValue || 0, intimacyLabel: levelLabel(INTIMACY_LEVELS, chat.intimacyValue || 0), enabled: chat.relationshipMode === true,
      },
    };
  },
  "chat.update": ({ id, ...data }) => {
    myChat(id);
    const patch = {};
    if (data.name !== undefined) patch.name = String(data.name).trim().slice(0, 40) || fail("กรุณากรอกชื่อแชท", "bad_request");
    ["isPinned", "isArchived", "isRead", "relationshipMode"].forEach((k) => { if (data[k] !== undefined) patch[k] = Boolean(data[k]); });
    if (data.personaId !== undefined) patch.personaId = data.personaId || null;
    if (data.scenarioId !== undefined) patch.scenarioId = data.scenarioId || null;
    if (data.modelId !== undefined) patch.modelId = modelOf(data.modelId).id;
    if (data.settings !== undefined) {
      const s = data.settings || {};
      patch.settings = { bannedWords: (s.bannedWords || []).slice(0, 50).map((w) => String(w).slice(0, 60)), langGuard: s.langGuard !== false, shortcuts: (s.shortcuts || []).slice(0, 3) };
    }
    return { chat: saveChat(id, patch) };
  },
  "chat.delete": ({ id }) => {
    myChat(id);
    saveChat(id, { isDeleted: true });
    return { ok: true };
  },
  "messages.list": ({ chatId, cursor, limit = 30 }) => {
    myChat(chatId);
    const all = msgsOf(chatId).filter((m) => !m.isDeleted);
    const end = cursor ? Math.max(0, all.findIndex((m) => m.id === cursor)) : all.length;
    const start = Math.max(0, end - limit);
    const items = all.slice(start, end);
    return { items, nextCursor: start > 0 && items.length ? items[0].id : null };
  },
  "chat.send": ({ chatId, content, modelId, replyToId }) => {
    const chat = myChat(chatId);
    const text = String(content || "").trim();
    if (!text) fail("กรุณาพิมพ์ข้อความ", "bad_request");
    if (text.length > 6000) fail("ข้อความยาวเกิน 6,000 ตัวอักษร", "bad_request");
    const model = modelOf(modelId || chat.modelId);
    if (me().tokensLeft < model.coinPerMessage) fail(`คอยน์ไม่พอ ต้องการ ${model.coinPerMessage} คอยน์ แต่มี ${me().tokensLeft} คอยน์`, "insufficient_coins");
    // ทดสอบ: ข้อความที่มี "!busy" = AI ไม่ว่าง ไม่หักคอยน์ (เหมือน GS ที่ไม่เขียนอะไรเมื่อ AI ล้ม)
    if (text.includes("!busy")) fail("ระบบ AI ไม่ว่างในขณะนี้ ลองใหม่อีกครั้ง", "ai_busy");
    const user = spend(model.coinPerMessage, "message", { chatId, characterId: chat.characterId, modelId: model.id, note: "ส่งข้อความ" });
    const now = Date.now();
    const msgs = msgsOf(chatId);
    const mine = { id: uid("m_"), chatId, sender: "user", type: "text", content: text, replyToId: replyToId || null, modelId: model.id, createdAt: new Date(now).toISOString() };
    const delta = { romantic: 2, intimacy: 3 };
    const reply = {
      id: uid("m_"), chatId, sender: "character", type: "text", content: AI_REPLIES[msgs.length % AI_REPLIES.length],
      romanticDelta: delta.romantic, intimacyDelta: delta.intimacy, modelId: model.id, coinCost: model.coinPerMessage, createdAt: new Date(now + 1).toISOString(),
    };
    writeStore(`msgs:${chatId}`, [...msgs, mine, reply]);
    const next = saveChat(chatId, {
      latestMessageTime: reply.createdAt, lastPreview: reply.content.slice(0, 60), messageCount: (chat.messageCount || 0) + 2,
      romanticValue: clampRel((chat.romanticValue || 0) + delta.romantic), intimacyValue: clampRel((chat.intimacyValue || 0) + delta.intimacy), ...streakUpdate(chat),
    });
    return { userMessage: mine, aiMessage: reply, coinCost: model.coinPerMessage, coins: user.tokensLeft, delta, chat: next, mock: true };
  },
  "chat.regenerate": ({ chatId, modelId }) => {
    const chat = myChat(chatId);
    const msgs = msgsOf(chatId).filter((m) => !m.isDeleted);
    const lastIdx = msgs.map((m) => m.sender).lastIndexOf("character");
    if (lastIdx < 1 || msgs[lastIdx - 1].sender !== "user") fail("ไม่มีข้อความให้เจนใหม่", "bad_request");
    const model = modelOf(modelId || chat.modelId);
    const user = spend(model.coinPerMessage, "message", { chatId, characterId: chat.characterId, modelId: model.id, note: "เจนข้อความใหม่" });
    const old = msgs[lastIdx];
    const reply = {
      ...old, id: uid("m_"), content: AI_REPLIES[(AI_REPLIES.indexOf(old.content) + 1 + AI_REPLIES.length) % AI_REPLIES.length], modelId: model.id,
      coinCost: model.coinPerMessage, reaction: null, createdAt: new Date().toISOString(),
    };
    writeStore(`msgs:${chatId}`, msgs.map((m) => (m.id === old.id ? reply : m)));
    saveChat(chatId, { lastPreview: reply.content.slice(0, 60) });
    return { aiMessage: reply, coinCost: model.coinPerMessage, coins: user.tokensLeft };
  },
  "message.react": ({ chatId, id, reaction }) => {
    myChat(chatId);
    const msgs = msgsOf(chatId).map((m) => (m.id === id ? { ...m, reaction: reaction === "like" ? "like" : null } : m));
    writeStore(`msgs:${chatId}`, msgs);
    return { message: msgs.find((m) => m.id === id) };
  },
  "memory.get": ({ chatId }) => {
    myChat(chatId);
    const m = readStore(`memory:${chatId}`, null);
    return { memory: m, limit: vipOf(me()).tier.noteLimit, used: m ? m.charCount : 0 };
  },
  "memory.save": ({ chatId, text, isActive }) => {
    myChat(chatId);
    const limit = vipOf(me()).tier.noteLimit;
    const t = String(text === undefined ? "" : text);
    if (t.length > limit) fail(`โน้ตยาวเกินโควตา (${t.length}/${limit} ตัวอักษร) อัปเกรดคุยคลับเพื่อเพิ่มโควตา`, "bad_request");
    const prev = readStore(`memory:${chatId}`, null);
    const m = { id: chatId, chatId, text: t, charCount: t.length, isActive: isActive === undefined ? prev?.isActive !== false : Boolean(isActive) };
    writeStore(`memory:${chatId}`, m);
    return { memory: m, limit };
  },
  "personas.list": ({ scope }) => ({ items: scope === "public" ? SAMPLE_PERSONAS : myPersonas() }),
  "personas.save": ({ id, name, detail }) => {
    const n = String(name || "").trim().slice(0, 40);
    if (!n) fail("กรุณากรอกชื่อบทบาท", "bad_request");
    const row = { id: id || uid("pe_"), userId: me().id, name: n, detail: String(detail || "").slice(0, 2000), visibility: "private" };
    writeStore("personas", id ? myPersonas().map((p) => (p.id === id ? { ...p, ...row } : p)) : [row, ...myPersonas()]);
    return { persona: row };
  },
  "scenarios.list": ({ scope, characterId }) => ({
    items: (scope === "public" ? SAMPLE_SCENARIOS : myScenarios()).filter((s) => !characterId || !s.characterId || s.characterId === characterId),
  }),
  "scenarios.save": ({ id, name, detail, characterId }) => {
    const n = String(name || "").trim().slice(0, 60);
    const d = String(detail || "").trim().slice(0, 3000);
    if (!n || !d) fail("กรุณากรอกชื่อและรายละเอียดสถานการณ์", "bad_request");
    const row = { id: id || uid("sc_"), userId: me().id, name: n, detail: d, characterId: characterId || null, visibility: "private" };
    writeStore("scenarios", id ? myScenarios().map((p) => (p.id === id ? { ...p, ...row } : p)) : [row, ...myScenarios()]);
    return { scenario: row };
  },
  "gift.send": async ({ chatId, giftId }) => {
    const chat = myChat(chatId);
    const gift = SAMPLE_GIFTS.find((x) => x.id === giftId) || fail("ไม่พบของขวัญ", "not_found");
    const user = spend(gift.coinCost, "gift", { chatId, characterId: chat.characterId, refId: gift.id, note: `ส่ง ${gift.nameTh}` });
    const now = Date.now();
    const prevGift = [...msgsOf(chatId)].reverse().find((m) => m.type === "gift" && m.sender === "user" && !m.isDeleted);
    const combo = nextCombo(prevGift, gift.id, now);
    const tier = giftTier(gift.coinCost);
    const message = {
      id: uid("m_"), chatId, sender: "user", type: "gift", giftId: gift.id, giftName: gift.nameTh, giftIcon: gift.icon, coinCost: gift.coinCost, tier, combo,
      content: `***ส่ง ${gift.nameTh} เป็นของขวัญ***`, createdAt: new Date(now).toISOString(),
    };
    // ตัวละครดีใจ: เลือกคำตอบ gift_* จากไฟล์ตามบุคลิก (เหมือนโหมด Firebase) ไม่ซ้ำ 3 ล่าสุด
    const recent = readStore(`giftrecent:${chatId}`, []);
    let text = `ขอบคุณสำหรับ${gift.nameTh}นะ ดีใจมากเลย`;
    try {
      const picked = await answerIntent(resolvePersona(charOf(chat.characterId)), giftIntent(gift.coinCost), { recent, userName: me().displayName, charName: charOf(chat.characterId)?.name, giftName: gift.nameTh });
      text = picked.reply;
      writeStore(`giftrecent:${chatId}`, [picked.key, ...recent.filter((x) => x !== picked.key)].slice(0, 3));
    } catch (e) { /* ใช้ข้อความสำรอง */ }
    const delta = chat.relationshipMode === true ? giftRelDelta(gift.coinCost) : { romantic: 0, intimacy: 0 };
    const aiMessage = { id: uid("m_"), chatId, sender: "character", type: "text", content: text, intent: giftIntent(gift.coinCost), romanticDelta: delta.romantic, intimacyDelta: delta.intimacy, createdAt: new Date(now + 1).toISOString() };
    writeStore(`msgs:${chatId}`, [...msgsOf(chatId), message, aiMessage]);
    const next = saveChat(chatId, {
      latestMessageTime: aiMessage.createdAt, lastPreview: aiMessage.content.slice(0, 60), messageCount: (chat.messageCount || 0) + 2,
      romanticValue: clampRel((chat.romanticValue || 0) + delta.romantic), intimacyValue: clampRel((chat.intimacyValue || 0) + delta.intimacy),
    });
    return {
      giftTx: { id: uid("gt_"), giftId: gift.id, coinCost: gift.coinCost }, message, aiMessage, coins: user.tokensLeft,
      gift: { id: gift.id, name: gift.nameTh, icon: gift.icon, coinCost: gift.coinCost, tier, combo }, delta, chat: next, intent: giftIntent(gift.coinCost),
    };
  },

  // ---- coins / club / topup ----------------------------------------------
  "vip.status": () => {
    const { claimable, month, ...v } = vipOf(me()); // eslint-disable-line no-unused-vars
    return v;
  },
  "vip.claimMonthly": () => {
    const u = me();
    const v = vipOf(u);
    if (!v.claimable.length) fail("ไม่มีคอยน์รายเดือนให้รับ", "already_done");
    const total = v.claimable.reduce((s, t) => s + t.monthlyCoins, 0);
    patchMe({ monthlyClaims: { [v.month]: [...((u.monthlyClaims || {})[v.month] || []), ...v.claimable.map((t) => t.id)] } });
    const next = earn(total, "monthly", { note: "คอยน์รายเดือนคุยคลับ" });
    return { coins: total, balance: next.tokensLeft };
  },
  "pricing.get": () => ({
    coin: { ...MOCK_PRICING.coin, bonusTiers: parseTiers(MOCK_PRICING.coin.bonusTiers) },
    create: { ...MOCK_PRICING.credit, tiers: parseTiers(MOCK_PRICING.credit.tiers), presets: creditPresets(MOCK_PRICING.credit) },
    createCredits: me().createCredits || 0, isSuper: me().user_type === "super_user",
  }),
  "creditPack.history": () => ({ items: [], nextCursor: null }),
  "creditPack.init": ({ transactionId, qty }) => {
    const calc = creditCalc(qty, MOCK_PRICING.credit);
    if (!calc.ok) fail(calc.reason, "bad_request");
    const all = readStore("topups", {});
    if (!all[transactionId]) {
      all[transactionId] = { id: transactionId, userId: me().id, packageId: "create_credit", kind: "create_credit", qty: calc.qty, amount: calc.price, coins: 0, points: 0, status: "pending", createdAt: new Date().toISOString() };
      writeStore("topups", all);
    }
    return { topup: { id: transactionId, status: all[transactionId].status, amount: calc.price, qty: calc.qty } };
  },
  "creditPack.confirm": ({ transactionId }) => {
    const all = readStore("topups", {});
    const t = all[transactionId];
    if (!t || t.userId !== me().id || t.kind !== "create_credit") fail("ไม่พบรายการซื้อสิทธิ์นี้", "bad_request");
    if (t.status === "credited") return { credited: true, already: true, qty: t.qty };
    if (!(window.__aitalkMockPaid || {})[transactionId]) fail("ยังไม่พบการชำระเงิน (สถานะ: pending)", "payment_pending");
    const next = patchMe({ createCredits: (me().createCredits || 0) + t.qty });
    all[transactionId] = { ...t, status: "credited", creditedAt: new Date().toISOString() };
    writeStore("topups", all);
    return { credited: true, qty: t.qty, added: t.qty, balance: next.createCredits };
  },
  "topup.init": ({ transactionId, packageId, amount }) => {
    let pkg;
    if (packageId === "custom") {
      const calc = coinCalc(amount, MOCK_PRICING.coin);
      if (!calc.ok) fail(calc.reason, "bad_request");
      pkg = { id: "custom", name: `กำหนดเอง ${calc.baht} บาท`, price: calc.baht, coins: calc.coins, vipPoints: calc.vipPoints };
    } else pkg = SAMPLE_PACKAGES.find((p) => p.id === packageId) || fail("ไม่พบแพ็กเกจ", "not_found");
    const all = readStore("topups", {});
    if (!all[transactionId]) {
      all[transactionId] = { id: transactionId, userId: me().id, packageId, amount: pkg.price, coins: pkg.coins, points: pkg.vipPoints, status: "pending", createdAt: new Date().toISOString() };
      writeStore("topups", all);
    }
    return { topup: { id: transactionId, status: all[transactionId].status, amount: pkg.price } };
  },
  // ยังไม่ได้จ่าย (ไม่ได้กด "จำลองจ่าย") -> payment_pending เหมือน GS
  "topup.confirm": ({ transactionId }) => {
    const all = readStore("topups", {});
    const t = all[transactionId];
    if (!t || t.userId !== me().id) fail("ไม่พบรายการเติมเงินนี้ (ต้องเรียก topup.init หลังสร้าง QR)", "bad_request");
    if (t.status === "credited") return { credited: true, already: true, coins: t.coins, points: t.points };
    if (!(window.__aitalkMockPaid || {})[transactionId]) fail("ยังไม่พบการชำระเงิน (สถานะ: pending)", "payment_pending");
    if (t.kind === "create_credit") fail("รายการนี้เป็นการซื้อสิทธิ์สร้างตัวละคร", "bad_request");
    const pkg = t.packageId === "custom" ? { id: "custom", name: `กำหนดเอง ${t.amount} บาท`, coins: t.coins, vipPoints: t.points } : SAMPLE_PACKAGES.find((p) => p.id === t.packageId);
    const user = creditPackage(pkg, transactionId);
    all[transactionId] = { ...t, status: "credited", creditedAt: new Date().toISOString() };
    writeStore("topups", all);
    return { credited: true, coins: pkg.coins, added: pkg.coins, points: pkg.vipPoints, balance: user.tokensLeft };
  },
  "topups.mine": () => ({ items: Object.values(readStore("topups", {})).filter((t) => t.userId === me().id).sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))), nextCursor: null }),
  "coins.historyList": ({ type }) =>
    readStore("coinTx", []).filter((t) => t.userId === me().id && (!type || type === "all" || (type === "in" ? t.coinUsed > 0 : t.coinUsed < 0))),
  "coins.history": ({ cursor, limit = 20, usageType }) => {
    const rows = readStore("coinTx", []).filter((t) => t.userId === me().id && (!usageType || t.usageType === usageType));
    const off = Number(cursor) || 0;
    return { items: rows.slice(off, off + limit), nextCursor: off + limit < rows.length ? String(off + limit) : null };
  },

  // ---- rewards ---------------------------------------------------------
  "checkin.status": () => {
    const u = me();
    const c = u.dailyCheckIn || {};
    const done = c.lastCheckInDate === today();
    const streak = streakNow(c);
    return {
      today: today(), canClaim: !done, streak, longestStreak: c.longestStreak || 0, lastCheckIn: c.lastCheckInDate || null,
      nextBonusIn: 10 - (streak % 10), todayReward: checkinReward(u, done ? streak : streak + 1),
      history: (c.checkInDates || []).filter((d) => d.slice(0, 7) === today().slice(0, 7)),
    };
  },
  "checkin.claim": () => {
    const u = me();
    const c = u.dailyCheckIn || { streak: 0, checkInDates: [] };
    if (c.lastCheckInDate === today()) fail("วันนี้เช็คอินไปแล้ว", "already_done");
    const streak = c.lastCheckInDate === thaiDay(-1) ? (c.streak || 0) + 1 : 1;
    const rw = checkinReward(u, streak);
    const next = earn(rw.base + rw.bonus, "checkin", { note: `เช็คอินวันที่ ${streak}` });
    patchMe({ dailyCheckIn: { streak, longestStreak: Math.max(streak, c.longestStreak || 0), lastCheckInDate: today(), checkInDates: [...(c.checkInDates || []), today()] } });
    return { coins: rw.base, bonus: rw.bonus, streak, balance: next.tokensLeft };
  },
  "promo.redeem": ({ code }) => {
    const clean = String(code || "").trim().toUpperCase();
    if (!/^[A-Z0-9_-]{4,40}$/.test(clean)) fail("โค้ดไม่ถูกต้อง", "bad_request");
    const codes = { WELCOME50: 50, AITALK100: 100 };
    if (!codes[clean]) fail("โค้ดไม่ถูกต้องหรือหมดอายุ", "bad_request");
    const used = readStore("redeemed", {});
    const key = `${me().id}:${clean}`;
    if (used[key]) fail("คุณใช้โค้ดนี้ไปแล้ว", "already_done");
    used[key] = true;
    writeStore("redeemed", used);
    const next = earn(codes[clean], "redeem", { note: `ใช้โค้ด ${clean}` });
    return { coins: codes[clean], balance: next.tokensLeft };
  },
  "referral.apply": ({ code }) => {
    const clean = String(code || "").trim().toUpperCase();
    if (!clean) fail("กรุณากรอกโค้ด", "bad_request");
    if (clean === me().invitationCode) fail("ใช้โค้ดของตัวเองไม่ได้", "bad_request");
    if (me().referredBy) fail("คุณใช้โค้ดชวนเพื่อนไปแล้ว", "already_done");
    const next = earn(50, "invite", { note: "กรอกโค้ดชวนเพื่อน" });
    patchMe({ referredBy: clean });
    return { coins: 50, balance: next.tokensLeft };
  },
  "referral.info": () => ({ code: me().invitationCode, invited: 0, maxInvites: 5, earned: 0, history: [], referredBy: me().referredBy || null }),

  // ---- notifications / feed -------------------------------------------
  "notif.list": () => {
    const read = readStore("notifRead", []);
    return [
      { id: "nt1", category: "system", title: "ยินดีต้อนรับสู่ AITalk", body: "รับคอยน์ฟรีทุกวันที่หน้า \"รางวัล\"", createdAt: new Date().toISOString() },
      { id: "nt2", category: "character", title: "ตัวละครใหม่จากครีเอเตอร์ที่คุณติดตาม", body: "ลูน่า นักดูดาว เปิดตัวแล้ว", createdAt: new Date(Date.now() - 36e5).toISOString() },
    ].map((n) => ({ ...n, isRead: read.includes(n.id) }));
  },
  "notif.read": ({ ids, all }) => {
    const list = all ? handlers["notif.list"]().map((n) => n.id) : ids || [];
    writeStore("notifRead", Array.from(new Set([...readStore("notifRead", []), ...list])));
    return { ok: true };
  },
  "feed.list": () => SAMPLE_POSTS.map((p) => ({ ...p, creator: creatorOf(p.userId), createdAt: new Date().toISOString() })),
  "shorts.list": () =>
    SAMPLE_CHARACTERS.slice(0, 4).map((c) => ({ id: `s_${c.id}`, characterId: c.id, name: c.name, emoji: c.emoji, colors: c.colors, caption: c.tagline, likeCount: c.likeCount })),

  // ---- admin (super_user) ---------------------------------------------
  "admin.stats": () => {
    if (me().user_type !== "super_user") fail("ไม่มีสิทธิ์");
    const users = Object.values(mockUsers.all());
    return {
      days: [],
      totals: {
        users: users.length, characters: SAMPLE_CHARACTERS.length + myCharacters().length, chats: readStore("chats", []).length,
        coinsInCirculation: users.reduce((s, u) => s + (u.tokensLeft || 0), 0),
      },
    };
  },
  "admin.users": () => {
    if (me().user_type !== "super_user") fail("ไม่มีสิทธิ์");
    return Object.values(mockUsers.all()).map(({ password, ...u }) => u); // eslint-disable-line no-unused-vars
  },
};

export const runMock = async (action, params = {}) => {
  await new Promise((r) => setTimeout(r, MOCK_LATENCY));
  const fn = handlers[action];
  if (!fn) throw new Error(`mock ยังไม่รองรับ action: ${action}`);
  return fn(params);
};
